'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { Mail, Phone, MapPin, Clock } from 'lucide-react'
import { sortByWeekOrder } from '@/lib/weekdays'

/* ─── Inline social icons ─────────────────────────────────────────────────── */
function IconFacebook({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>
  )
}

function IconInstagram({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
    </svg>
  )
}

interface ScheduleDay {
  dayIndex: number
  day: string
  isOpen: boolean
  openTime: string
  closeTime: string
}

/* ─── Footer ──────────────────────────────────────────────────────────────── */
export default function Footer() {
  const pathname = usePathname()
  const [schedule, setSchedule] = useState<ScheduleDay[]>([])
  useEffect(() => {
    if (pathname?.startsWith('/admin')) return
    fetch('/api/store/schedule')
      .then((r) => r.ok ? r.json() : [])
      .then((data: ScheduleDay[]) => setSchedule(data))
      .catch(() => {})
  }, [pathname])

  if (pathname?.startsWith('/admin')) return null

  const now      = new Date()
  const todayIdx = now.getDay() // 0=Sunday … 6=Saturday — matches dayIndex

  // Weekvolgorde komt uit @/lib/weekdays (zondag eerst)
  const sorted = sortByWeekOrder(schedule)

  return (
    <footer className="bg-[#2C1810] text-[#F4A460]" aria-label="Voettekst">
      {/* Main footer body */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-12">

          {/* Column 1: Brand + tagline + description + social */}
          <div>
            <Link href="/" className="flex items-center gap-2.5 group mb-5">
              <span className="w-10 h-10 flex items-center justify-center rounded-full overflow-hidden transition-colors shrink-0" style={{ background: '#8B4513' }} onMouseEnter={(e) => (e.currentTarget.style.background = '#D2691E')} onMouseLeave={(e) => (e.currentTarget.style.background = '#8B4513')}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src="/images/logo-solo.png" alt="" aria-hidden="true" style={{ width: 30, height: 30, objectFit: 'contain', filter: 'brightness(0) invert(1) sepia(0.15) saturate(1.2)' }} />
              </span>
              <div className="leading-none">
                <span className="font-playfair font-bold text-[#FDF6EC] text-lg block group-hover:text-[#F4A460] transition-colors">
                  Bakkerij Voortman
                </span>
                <span className="font-dancing text-[#D2691E] text-sm block -mt-0.5">
                  Gebakken met liefde
                </span>
              </div>
            </Link>

            <p className="text-sm text-[#F4A460]/70 leading-relaxed mb-5">
              Al bijna 80 jaar bakken wij dagelijks het lekkerste brood, gebak en banket voor de
              inwoners van Rijssen en omgeving. Onze bakkers staan elke dag vroeg op om u het
              allerbeste te kunnen brengen.
            </p>

            <p className="font-dancing text-[#DAA520] text-lg mb-5">
              Gebakken met liefde, geserveerd met trots
            </p>

            {/* Social icons */}
            <div className="flex gap-3">
              <a
                href="https://www.facebook.com/bakkerijvoortman"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-[#1877F2] text-[#F4A460] hover:text-white transition-all"
                aria-label="Volg ons op Facebook"
              >
                <IconFacebook size={17} />
              </a>
              <a
                href="https://www.instagram.com/bakkerijvoortman"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-gradient-to-br hover:from-[#E1306C] hover:to-[#833AB4] text-[#F4A460] hover:text-white transition-all"
                aria-label="Volg ons op Instagram"
              >
                <IconInstagram size={17} />
              </a>
              <a
                href="mailto:info@bakkerijvoortmanrijssen.nl"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-[#D2691E] text-[#F4A460] hover:text-white transition-all"
                aria-label="Stuur ons een e-mail"
              >
                <Mail size={17} />
              </a>
            </div>
          </div>

          {/* Column 2: Openingstijden */}
          <div>
            <h3 className="font-playfair font-bold text-base mb-4 flex items-center gap-2" style={{ color: '#FDF6EC' }}>
              <Clock size={16} style={{ color: '#D2691E' }} />
              Openingstijden
            </h3>
            <ul className="space-y-1.5">
              {sorted.map((item) => {
                const isToday = item.dayIndex === todayIdx
                const hours = item.isOpen && item.openTime && item.closeTime
                  ? `${item.openTime} – ${item.closeTime}`
                  : 'Gesloten'
                return (
                  <li
                    key={item.dayIndex}
                    className="flex items-center justify-between text-sm py-1 px-2 rounded-lg transition-colors"
                    style={{
                      background: isToday ? 'rgba(139,69,19,0.4)' : 'transparent',
                      color: isToday ? '#FDF6EC' : '#D2691E',
                    }}
                  >
                    <span className="font-medium">
                      {item.day}
                      {isToday && (
                        <span className="ml-1.5 text-[10px] bg-[#D2691E] text-white px-1.5 py-0.5 rounded-full">
                          vandaag
                        </span>
                      )}
                    </span>
                    <span style={{ color: !item.isOpen ? '#f87171' : 'inherit' }}>{hours}</span>
                  </li>
                )
              })}
            </ul>
          </div>

          {/* Column 3: Contact info */}
          <div>
            <h3 className="font-playfair font-bold text-base mb-4" style={{ color: '#FDF6EC' }}>
              Contact
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-2.5 text-sm text-[#F4A460]/80">
                <MapPin size={16} className="text-[#D2691E] mt-0.5 shrink-0" />
                <address className="not-italic leading-snug">
                  Boomkamp 17<br />
                  7461 AW Rijssen
                </address>
              </li>
              <li>
                <a
                  href="tel:+31548512752"
                  className="flex items-center gap-2.5 text-sm text-[#F4A460]/80 hover:text-[#F4A460] transition-colors"
                >
                  <Phone size={16} className="text-[#D2691E] shrink-0" />
                  0548 – 51 27 52
                </a>
              </li>
              <li>
                <a
                  href="mailto:info@bakkerijvoortmanrijssen.nl"
                  className="flex items-center gap-2.5 text-sm text-[#F4A460]/80 hover:text-[#F4A460] transition-colors break-all"
                >
                  <Mail size={16} className="text-[#D2691E] shrink-0" />
                  info@bakkerijvoortmanrijssen.nl
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#F4A460]/50">
          <p>
            © 2026 Bakkerij Voortman Rijssen. Alle rechten voorbehouden.
          </p>
          <p className="font-dancing text-sm text-[#DAA520]/70">
            Opgericht in 1946 – Bijna 80 jaar bakkersvakmanschap
          </p>
          <nav className="flex gap-4" aria-label="Juridische links">
            <Link href="/privacy" className="hover:text-[#F4A460] transition-colors">
              Privacybeleid
            </Link>
            <Link href="/voorwaarden" className="hover:text-[#F4A460] transition-colors">
              Algemene voorwaarden
            </Link>
          </nav>
        </div>
      </div>
    </footer>
  )
}
